<script setup lang="ts">
/**
 * Bottom of the thread, where the AI's next bubble will appear (aligned like AI bubbles): "AI กำลังคิดคำตอบ" with
 * soft dots while the sales brain works, "AI กำลังพิมพ์ · จะส่งในไม่กี่วินาที" during the 5–15 s before a bubble is
 * sent, gone when it lands (stores/aimode.ts busyOf · lib/aiBusy.ts). Staff only: the customer sees their own
 * platform's typing indicator.
 */
import { computed } from 'vue'
import { BUSY_TEXT } from '../../lib/aiBusy'
import { useAiMode } from '../../stores/aimode'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ convId: string }>()
const aimode = useAiMode()
const state = computed(() => aimode.busyOf(props.convId))
</script>

<template>
  <div v-if="state" class="abr" role="status" aria-live="polite" :data-state="state">
    <div class="abr-b">
      <span class="abr-t">{{ BUSY_TEXT[state] }}</span>
      <span class="dots" aria-hidden="true"><i></i><i></i><i></i></span>
    </div>
    <span class="av av-sm av-ai" aria-hidden="true"><Icon name="sparkle" /></span>
  </div>
</template>

<style scoped>
.abr {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 8px;
}
.abr-b {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: min(448px, 78%);
  padding: 8px 13px;
  border-radius: 14px;
  border-bottom-right-radius: 5px;
  background: #f1f8f4;
  border: 1px dashed #9fcdb8;
  color: #2d5a4b;
  font-size: 13px;
  line-height: 20px;
}
.abr-t {
  min-width: 0;
}
.av-sm {
  width: 28px;
  height: 28px;
  font-size: 12px;
}
.av-sm :deep(.ic) {
  width: 15px;
  height: 15px;
}
.dots {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.dots i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--ac);
  opacity: 0.3;
  animation: abr-dot 1.2s ease-in-out infinite;
}
.dots i:nth-child(2) {
  animation-delay: 0.2s;
}
.dots i:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes abr-dot {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-4px);
  }
}
/* owner 27 ก.ย. 69 "ทำให้มี animate เวลาคิดด้วย": the words shimmer, the AI avatar breathes with a soft halo */
.abr-t {
  background: linear-gradient(90deg, #2d5a4b 0%, #2d5a4b 35%, #6cc49c 50%, #2d5a4b 65%, #2d5a4b 100%);
  background-size: 220% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: abr-shimmer 1.8s linear infinite;
}
@keyframes abr-shimmer {
  from {
    background-position: 110% 0;
  }
  to {
    background-position: -110% 0;
  }
}
.av-ai {
  animation: abr-breathe 1.6s ease-in-out infinite;
}
.av-ai :deep(.ic) {
  animation: abr-spark 1.6s ease-in-out infinite;
}
@keyframes abr-breathe {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(47, 158, 110, 0.35);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(47, 158, 110, 0);
  }
}
@keyframes abr-spark {
  0%,
  100% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(20deg) scale(1.15);
  }
}
.abr[data-state='typing'] .abr-t {
  animation-duration: 1.2s;
}
/* no movement for people who asked for less motion — a gentle fade still shows the AI is working */
@media (prefers-reduced-motion: reduce) {
  .abr-t {
    animation: none;
    color: #2d5a4b;
    background: none;
  }
  .av-ai :deep(.ic) {
    animation: none;
  }
  .dots i {
    animation: abr-fade 1.4s ease-in-out infinite;
    transform: none;
  }
  .av-ai {
    animation: abr-fade 1.6s ease-in-out infinite;
  }
}
@keyframes abr-fade {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
}
</style>
